import type { PassoDeProgresso } from '@/ui/ProgressTimeline';

import type { EventoDeAcompanhamento, Inscricao } from './contrato';

/**
 * As etapas do bloco "Acompanhamento". São sempre quatro, com os nomes do
 * protótipo: as três primeiras fixas, e a quarta vira "Reprovada" ou
 * "Desistente" quando o processo terminou assim.
 *
 * As datas vêm dos eventos reais que a API devolve (0127). Duas regras
 * adotadas na implementação, a confirmar com a PO (ver "Pontos a verificar"
 * no README deste diretório):
 *   * `HABILITADO` conta como aprovado, como `PRE_CREDENCIADO`;
 *   * "Documentos Verificados" fica concluída quando a inscrição foi aprovada —
 *     não há análise por documento no fluxo real, o DTP decide a inscrição
 *     inteira.
 * "Credenciado criado e convite enviado" só aparece na habilitação: no
 * pré-credenciamento isso ainda não aconteceu.
 */
export function etapasDoCredenciamento(
  inscricao: Pick<Inscricao, 'status_inscricao' | 'data_envio' | 'data_analise' | 'acompanhamento'>,
): PassoDeProgresso[] {
  const status = inscricao.status_inscricao;
  const habilitada = status === 'HABILITADO';
  const aprovada = status === 'PRE_CREDENCIADO' || habilitada;
  const reprovada = status === 'REPROVADO';
  const desistente = status === 'DESISTENTE';
  const emAnalise = status === 'EM_ANALISE';
  const encerrada = aprovada || reprovada || desistente;

  const quando = (origem: EventoDeAcompanhamento['origem'], etapa: string) =>
    ultimaData(inscricao.acompanhamento, origem, etapa);

  const inicioDaAnalise = quando('ANALISE', 'ANALISE_INICIADA') ?? inscricao.data_analise;
  const decisao =
    inscricao.data_analise ?? quando('ANALISE', 'APROVADA') ?? quando('ANALISE', 'REPROVADA');

  const etapas: PassoDeProgresso[] = [
    {
      label: 'Inscrição Enviada',
      status: 'completed',
      date: inscricao.data_envio ?? quando('ENVIO', 'ENVIADA'),
      description: 'Formulário e documentos submetidos',
    },
    {
      label: 'Em Análise',
      status: emAnalise ? 'current' : encerrada ? 'completed' : 'pending',
      date: emAnalise || encerrada ? inicioDaAnalise : undefined,
      description: emAnalise ? 'Analista designado' : undefined,
    },
    {
      label: 'Documentos Verificados',
      status: aprovada ? 'completed' : 'pending',
      description: emAnalise ? 'Aguardando verificação dos documentos' : undefined,
    },
  ];

  if (reprovada) {
    etapas.push({ label: 'Reprovada', status: 'error', date: decisao });
  } else if (desistente) {
    etapas.push({
      label: 'Desistente',
      status: 'error',
      date: quando('CONVOCACAO', 'DESISTENCIA') ?? decisao,
    });
  } else {
    etapas.push({
      label: 'Credenciamento Aprovado',
      status: aprovada ? 'completed' : 'pending',
      date: habilitada
        ? (quando('CONVOCACAO', 'HABILITADA') ?? decisao)
        : aprovada
          ? decisao
          : undefined,
      description: habilitada ? 'Credenciado criado e convite enviado' : undefined,
    });
  }

  return etapas;
}

/** Data do último evento daquele tipo, ou `null` se ainda não aconteceu. */
function ultimaData(
  eventos: EventoDeAcompanhamento[],
  origem: EventoDeAcompanhamento['origem'],
  etapa: string,
): string | null {
  const encontrados = eventos.filter(
    (evento) => evento.origem === origem && evento.etapa === etapa,
  );
  return encontrados.at(-1)?.em ?? null;
}
